import type { ComponentType } from 'react';

import type { PartyType, VendorKind } from '@/lib/auth/session';
import { menuFor } from '@/lib/menu';
import { SiteHeroCard } from '@/pages/home/SiteHeroCard';
import {
  ClaimWidget,
  ContractWidget,
  DailyLogWidget,
  DocumentWidget,
  DrawingWidget,
  EquipmentWidget,
  InspectionWidget,
  LaborWidget,
  NcrWidget,
  ProgressWidget,
  PurchaseWidget,
  SafetyInspectionWidget,
  SafetyWidget,
  SupervisionWidget,
  TestReportWidget,
  WorkerWidget,
  type WidgetProps,
} from '@/pages/home/widgets/cards';

/**
 * 홈에 놓을 수 있는 위젯 목록. <b>여기가 유일한 정의</b>다 — front 의 `registry.ts` 와 같은 자리.
 *
 * 위젯 목록과 위젯 관리 모달의 목록을 따로 적으면, 위젯을 하나 더했을 때 반드시 한쪽만 갱신되는
 * 날이 온다. 홈(`HomePage`)에는 위젯 이름이 한 줄도 없다 — 전부 여기서 온다.
 *
 * ## 노출 규칙
 *
 * `parties` 는 <b>인가가 아니다.</b> 카탈로그에서 감추는 것은 UX 이고, 위젯이 실제로 쓰는 데이터는
 * 각 API 가 요청마다 다시 판정한다(`ExternalScope`). front 의 `requiresMenuKey` 자리에
 * 「누가 볼 수 있는 위젯인가」를 둔 것이다 — 협력사 전용(TBM·기성·계약)과 감리 전용은 여기서 갈린다.
 *
 * `fixed` 는 사용자가 끌 수 없는 위젯이다. 현장 머리(`site`)가 그렇다 — 「여기가 어디인가」는
 * 내가 그 위젯을 켰는지에 좌우되면 안 된다(DOC-84 §7 ③ 의 기울어진 쪽).
 *
 * ## 키는 메뉴 키와 같다
 *
 * `widgetKey` 는 `lib/menu.ts` 의 항목 키(경로 마디)와 같다 — 위젯의 「전체보기」가 가리키는
 * 메뉴가 곧 그 항목이다. 이 정합은 테스트가 지킨다(`registry.test.ts`).
 *
 * ## 순서가 곧 기본 배치다
 *
 * 위에 있는 것이 오늘 해야 할 일이고 아래로 갈수록 참고 자료다. 협력사는 안전(TBM 서명)이
 * 맨 위다 — 작업 전에 끝나야 하는 유일한 항목이라서. 감리에게는 그 위젯이 없어 검측이 첫 칸이다.
 */

export type WidgetSize = 'sm' | 'wide';

export type WidgetDefinition = {
  widgetKey: string;
  title: string;
  /** 위젯 관리 모달에서 무엇을 보여주는 위젯인지 설명한다. */
  description: string;
  icon: string;
  fixed: boolean;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
  /** 아직 손대지 않은 사용자에게 처음부터 보일지. */
  defaultVisible: boolean;
  /** 누가 볼 수 있는 위젯인가. 여기 없는 주체에게는 카탈로그에도 내놓지 않는다. */
  parties: PartyType[];
  /**
   * 「전체보기」가 가리키는 메뉴 키. 위젯 키와 같으면 적지 않는다 — 한 화면의 탭을 가리키는 위젯
   * (투입 인력 → 출역·투입 인력의 둘째 탭)만 적는다. 정합 테스트가 이 값으로 메뉴를 찾는다.
   */
  menuKey?: string;
  Component: ComponentType<WidgetProps>;
};

// 직원(STAFF)도 본다 — 협력사 전용 셋(TBM·기성·계약)만 빠진다. 규칙은 `lib/menu.ts` 와 같다(DOC-87 §3-8).
const EVERYONE: PartyType[] = ['VENDOR', 'SUPERVISOR', 'OWNER', 'STAFF'];
const VENDOR_ONLY: PartyType[] = ['VENDOR'];

export const WIDGET_DEFINITIONS: WidgetDefinition[] = [
  {
    // 현장 머리 — 「여기가 어디이고 지금 어디까지 왔나」. 늘 첫 칸이고 끌 수 없다.
    // 크기는 wide 하나뿐이다: 숫자 셋과 사진이 반 칸에 들어가지 않는다.
    widgetKey: 'site',
    title: '현장',
    description: '현장 이름·발주처·공사기간과 전체 공정율·지연 공정·준공까지 남은 날을 보여줍니다.',
    icon: 'info',
    fixed: true,
    sizes: ['wide'],
    defaultSize: 'wide',
    defaultVisible: true,
    parties: EVERYONE,
    Component: SiteHeroCard,
  },
  {
    widgetKey: 'safety',
    title: '안전',
    description: '오늘 TBM 의 위험요인과 우리 업체의 서명 현황을 보여줍니다.',
    icon: 'lock',
    fixed: false,
    sizes: ['sm', 'wide'],
    // 작업 전에 끝나야 하는 유일한 항목이라 넓게 시작한다 — 위험요인 문장이 잘리면 안 읽는다.
    defaultSize: 'wide',
    defaultVisible: true,
    parties: VENDOR_ONLY,
    Component: SafetyWidget,
  },
  {
    widgetKey: 'inspections',
    title: '검측',
    description: '협력사에게는 불합격으로 재검을 걸어야 할 건, 감리에게는 판정할 건이 위로 옵니다.',
    icon: 'check',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: InspectionWidget,
  },
  {
    widgetKey: 'progress',
    title: '공정',
    description: '전체 공정율과 계획에 뒤진 공정을 보여줍니다.',
    icon: 'trending',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: ProgressWidget,
  },
  {
    widgetKey: 'safety-inspections',
    title: '안전 점검',
    description: '시정 지시 중 기한을 넘긴 것과 조치·확인이 남은 것을 보여줍니다.',
    icon: 'alert',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: SafetyInspectionWidget,
  },
  {
    widgetKey: 'ncrs',
    title: '부적합',
    description: '회신해야 할 부적합(협력사)과 회신을 확인할 부적합(감리)을 보여줍니다.',
    icon: 'flag',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: NcrWidget,
  },
  {
    widgetKey: 'test-reports',
    title: '시험성적서',
    description: '불합격 성적서와 판정 대기 중인 성적서를 보여줍니다.',
    icon: 'flask',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: TestReportWidget,
  },
  {
    widgetKey: 'drawings',
    title: '시공도·자재',
    description: '반려된 도면·자재 승인원(협력사)과 검토할 제출분(감리)을 보여줍니다.',
    icon: 'grid',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: DrawingWidget,
  },
  {
    widgetKey: 'claims',
    title: '기성',
    description: '최근 회차의 청구 금액과 누계율을 보여줍니다.',
    icon: 'chart',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: VENDOR_ONLY,
    Component: ClaimWidget,
  },
  {
    widgetKey: 'purchase',
    title: '구매·납품',
    description: '납품이 남은 발주(협력사)와 검수 대기 납품(감리)을 보여줍니다.',
    icon: 'truck',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: PurchaseWidget,
  },
  {
    widgetKey: 'labor',
    title: '출역',
    description: '오늘 출역 인원을 구역별로 보여줍니다.',
    icon: 'users',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: LaborWidget,
  },
  {
    widgetKey: 'equipment',
    title: '장비 가동',
    description: '오늘 장비 가동시간과 확인이 남은 가동일보 수를 보여줍니다.',
    icon: 'wrench',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: EquipmentWidget,
  },
  {
    widgetKey: 'workers',
    title: '투입 인력',
    description: '현재 투입 인원과 안전교육·건강진단이 빠진 사람을 보여줍니다.',
    icon: 'users',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    menuKey: 'labor',
    Component: WorkerWidget,
  },
  {
    widgetKey: 'daily',
    title: '작업일보',
    description: '가장 최근 일보의 작업 내용과 실적 줄을 보여줍니다.',
    icon: 'clipboard',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: DailyLogWidget,
  },
  {
    widgetKey: 'supervision',
    title: '감리일지',
    description: '가장 최근 감리일지의 공사 현황과 지시사항을 보여줍니다.',
    icon: 'clipboard',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: SupervisionWidget,
  },
  {
    widgetKey: 'contract',
    title: '계약',
    description: '주 계약의 금액과 착공·준공일을 보여줍니다.',
    icon: 'layout',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: VENDOR_ONLY,
    Component: ContractWidget,
  },
  {
    widgetKey: 'documents',
    title: '문서',
    description: '받은 문서 중 아직 열지 않은 것을 위로 보여줍니다.',
    icon: 'news',
    fixed: false,
    sizes: ['sm', 'wide'],
    defaultSize: 'sm',
    defaultVisible: true,
    parties: EVERYONE,
    Component: DocumentWidget,
  },
];

const BY_KEY = new Map(WIDGET_DEFINITIONS.map((definition) => [definition.widgetKey, definition] as const));

export function widgetByKey(widgetKey: string): WidgetDefinition | null {
  return BY_KEY.get(widgetKey) ?? null;
}

/**
 * 이 주체가 쓸 수 있는 위젯 — 위젯 관리 모달의 카탈로그이자 기본 배치의 재료다.
 *
 * <p>갈래(외주·구매·용역)는 메뉴가 정한다 — 위젯의 「전체보기」가 가리키는 메뉴가 그 갈래에 없으면 위젯도
 * 없다. 규칙을 여기 한 벌 더 적지 않는다(`lib/menu.ts` 의 `kinds`).
 */
export function widgetsFor(partyType: PartyType, kinds: VendorKind[] = []): WidgetDefinition[] {
  const allowed = new Set(menuFor(partyType, kinds).flatMap((group) => group.items.map((item) => item.key)));
  return WIDGET_DEFINITIONS.filter(
    (definition) =>
      definition.parties.includes(partyType) &&
      (definition.fixed || allowed.has(definition.menuKey ?? definition.widgetKey)),
  );
}
